import { Tooltip } from '@unmind/design-system';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tracking } from 'App/Tracking';
import { dateWithin24Hours } from 'Book/shared/utils';
import { talkEventsQuery_unmindEvents_edges_node as Event } from 'Services/BeGateway/Talk/__generated__/talkEventsQuery';

import { useUserEntitlements } from 'hooks';
import type { TType } from '../../types';
import { CancelCoachingAndTherapyAppointment } from '../CancelCoachingAndTherapyAppointment';
import { CancelCoachingAndTherapyRequest } from '../CancelCoachingAndTherapyRequest';
import {
  CancelEAPAppointmentModal,
  IDetailsProps,
} from '../CancelEAPAppointmentModal';
import { CancelButton, TooltipWrapper } from './styles';

export interface IProps {
  details: IDetailsProps;
  startDate?: Date;
  type: TType;
  onCancel?(): void;
}

export default ({ details, startDate, type, onCancel }: IProps) => {
  const { t } = useTranslation('book');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const {
    entitlements: { eapCanBookAppointments },
  } = useUserEntitlements();
  const ref = useRef<HTMLButtonElement>(null);

  const cancelButtonProps = {
    className: 'cancel-button',
    'data-long-text': t('service.cancelModal.ctas.cancel.long'),
    children: <span>{t('service.cancelModal.ctas.cancel.short')}</span>,
  };

  const isEapSession =
    typeof type === 'object' && type.__typename === 'BookableService';
  const eapBookingDisabled = !eapCanBookAppointments && isEapSession;

  return (
    <>
      {(startDate && dateWithin24Hours(startDate)) || eapBookingDisabled ? (
        <TooltipWrapper className="cancel-button-wrapper">
          <Tooltip
            triggerElement={
              <CancelButton disabled={true} {...cancelButtonProps} />
            }
            tooltipContents={t(
              `service.cancelModal.tooltip.${
                eapBookingDisabled ? 'unauthorized' : 'within24h'
              }`,
            )}
            placement="bottom"
            accessibilityId="bottom-tooltip"
          />
        </TooltipWrapper>
      ) : (
        <CancelButton
          ref={ref}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setCancelModalOpen(true);
            if (isEapSession) {
              tracking.track('book_eap_service_cancel_button_clicked');
            }
          }}
          {...cancelButtonProps}
        />
      )}

      {isEapSession ? (
        <CancelEAPAppointmentModal
          details={details}
          functions={{
            onClose: () => {
              setCancelModalOpen(false);
            },
            onCancel: () => {
              if (onCancel) onCancel();
              tracking.track('book_eap_service_cancelled');
            },
          }}
          isOpen={cancelModalOpen}
          parentRef={ref}
        />
      ) : (
        <></>
      )}

      {type === 'appointment' ? (
        <CancelCoachingAndTherapyAppointment
          event={
            {
              id: details.appointmentId,
              beginsAt: details.date,
            } as Event
          }
          open={cancelModalOpen}
          onCancelEventSuccess={onCancel}
          onClose={() => {
            setCancelModalOpen(false);
          }}
        />
      ) : (
        <></>
      )}

      {type === 'request' ? (
        <CancelCoachingAndTherapyRequest
          eventRequestID={details.appointmentId}
          open={cancelModalOpen}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onCancelEventSuccess={onCancel}
        />
      ) : (
        <></>
      )}
    </>
  );
};
